"use client";

import { useEffect, useMemo, useState } from "react";
import { useTopDestination } from "@/hooks/top-destination/useTopDestination";
import { useHotelNameSuggestions } from "@/hooks/hotel/useHotelNameSuggestions";
import type { GetTopDestinationSvcType } from "@/actions/destination.action";
import type { LocationComboboxOption } from "@/components/LocationCombobox";

type UseLocationSuggestionsParams = {
  svcType: GetTopDestinationSvcType;
  /** Raw text typed in the location input */
  search: string;
  /** Enable hotel-name suggestions (hotel service only) */
  enableHotelSuggestions: boolean;
  start?: string;
  end?: string;
  adults: number;
  children: number;
  rooms: number;
  destinationLimit?: number;
};

const MIN_HOTEL_SEARCH_LENGTH = 2;

/**
 * Shared location suggestions (top destinations + hotel-name suggestions)
 * used by SearchModule and Book for Me form.
 */
export const useLocationSuggestions = ({
  svcType,
  search,
  enableHotelSuggestions,
  start = "",
  end = "",
  adults,
  children,
  rooms,
  destinationLimit = 10,
}: UseLocationSuggestionsParams) => {
  // 1. Top destinations
  const { data: topDestinationData, isLoading: isLoadingDestinationOptions } =
    useTopDestination({ svcType, limit: destinationLimit });

  const destinationOptions: LocationComboboxOption[] = useMemo(
    () =>
      (topDestinationData?.data ?? []).map((item) => ({
        id: String(item.id),
        label: item.name,
        country: item.located ?? "",
        image: item.thumbnail ?? undefined,
        totalItem: item.metadata?.total ?? null,
        svcType,
        kind: "destination",
      })),
    [topDestinationData, svcType],
  );

  // 2. Hotel suggestions debouncer
  const [debouncedHotelSearchQuery, setDebouncedHotelSearchQuery] = useState(
    search.trim(),
  );

  useEffect(() => {
    if (!enableHotelSuggestions) {
      setDebouncedHotelSearchQuery("");
      return;
    }
    const handle = window.setTimeout(() => {
      setDebouncedHotelSearchQuery(search.trim());
    }, 300);
    return () => window.clearTimeout(handle);
  }, [search, enableHotelSuggestions]);

  const isSearchingHotels =
    enableHotelSuggestions &&
    debouncedHotelSearchQuery.length >= MIN_HOTEL_SEARCH_LENGTH;

  const shouldFetchHotelSuggestions =
    isSearchingHotels && Boolean(start) && Boolean(end);

  const {
    data: hotelSuggestionResponse,
    isFetching: isFetchingHotelSuggestions,
  } = useHotelNameSuggestions({
    search: debouncedHotelSearchQuery,
    start,
    end,
    adults,
    children,
    rooms,
    enabled: shouldFetchHotelSuggestions,
  });

  const hotelSuggestionOptions: LocationComboboxOption[] = useMemo(
    () =>
      (hotelSuggestionResponse?.data.items ?? []).map((item) => ({
        id: `hotel:${item.id}`,
        label: item.title,
        country: item.location?.address ?? "",
        image: item.primary ?? undefined,
        kind: "hotel",
        provinceId:
          item.location?.provinceId != null
            ? String(item.location.provinceId)
            : undefined,
      })),
    [hotelSuggestionResponse],
  );

  // 3. Merged options
  const comboboxOptions: LocationComboboxOption[] = useMemo(
    () =>
      isSearchingHotels
        ? [...hotelSuggestionOptions, ...destinationOptions]
        : destinationOptions,
    [isSearchingHotels, destinationOptions, hotelSuggestionOptions],
  );

  return {
    destinationOptions,
    hotelSuggestionOptions,
    comboboxOptions,
    isSearchingHotels,
    isComboboxLoading:
      isLoadingDestinationOptions ||
      (shouldFetchHotelSuggestions && isFetchingHotelSuggestions),
    sectionTitle: isSearchingHotels ? "Search results" : "Popular destinations",
  };
};
